/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { styles } from '../PRIVATE_Chart/themes/ChartStyles.css';
import { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { classNames } from '../utils/UNSAFE_classNames';

type BarProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  isSelected: boolean;
  isFocused: boolean;
  isDimmed: boolean;
  activeId?: string;
  isPointerEnabled: boolean;
  fill: ColorProps['color'];
  stroke?: ColorProps['color'];
  strokeWidth?: number;
  seriesIndex: number;
  groupIndex: number;
  'aria-label'?: string;
};

const HOVERED_INNER_INSET = 1.5;
const SELECTED_INNER_INSET = 2.5;

/**
 * The rect representing the bar item.
 * @param props
 * @returns
 */
export function Bar(props: BarProps) {
  const isInteractive = props.isSelected || props.isFocused;

  if (isInteractive) {
    return <InteractiveBar {...props} />;
  }

  return (
    <rect
      x={props.x}
      y={props.y}
      data-oj-series-index={props.seriesIndex}
      data-oj-group-index={props.groupIndex}
      data-oj-object="bar"
      aria-label={props['aria-label']}
      role={props['aria-label'] ? 'img' : undefined}
      id={props.activeId}
      style={{
        width: props.width,
        height: props.height,
        fill: props.fill,
        stroke: props.stroke,
        strokeWidth: props.strokeWidth
      }}
      class={classNames([
        props.isPointerEnabled ? styles.cursorEnable : undefined,
        props.isDimmed ? styles.dimmed : undefined
      ])}
    />
  );
}

/**
 * The selected or hovered selected bar.
 */
export function InteractiveBar(props: BarProps) {
  const { x, y, width, height, fill, isSelected, isFocused } = props;
  const inset = isSelected ? SELECTED_INNER_INSET : HOVERED_INNER_INSET;
  return (
    <g
      id={props.activeId}
      data-oj-object="bar"
      data-oj-series-index={props.seriesIndex}
      data-oj-group-index={props.groupIndex}
      className={props.isDimmed ? styles.dimmed : ''}
      role={props['aria-label'] ? 'img' : undefined}
      aria-label={props['aria-label']}>
      <rect
        x={x}
        y={y}
        class={classNames([isSelected ? styles.selectedBorder : ''])}
        style={{
          width: width,
          height: height,
          fill: !isSelected && isFocused ? fill : undefined
        }}></rect>
      <rect
        x={x + inset}
        y={y + inset}
        class={classNames([
          styles.contrastBorder,
          props.isPointerEnabled ? styles.cursorEnable : undefined
        ])}
        style={{
          width: width - 2 * inset,
          height: height - 2 * inset,
          fill: fill
        }}></rect>
    </g>
  );
}
